import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Compass, Check, ArrowRight } from "lucide-react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import { setIntent } from "../lib/nav";
import { OPEN_ROLES, ASSOCIATE_TRACK } from "../data/content";

const EnglishGate = () => (
    <div
        className="mb-10 p-4 rounded-xl flex items-start gap-3"
        style={{
            background: "linear-gradient(135deg, rgba(212,168,87,0.06), rgba(20,20,26,0.5))",
            border: "1px solid rgba(212,168,87,0.22)",
        }}
        data-testid="english-gate"
    >
        <div className="w-9 h-9 rounded-lg bg-gold/10 border border-gold/25 flex items-center justify-center shrink-0 font-display text-base text-gold">
            En
        </div>
        <div className="flex-1 text-sm leading-snug">
            <span className="text-gold font-medium">English fluency is required</span>
            <span className="text-ivory/70"> across every IndoBlitz track, internship and staff role. We do not teach English - please come prepared.</span>
        </div>
    </div>
);

const CareersPage = () => (
    <PageLayout title="Careers · IndoBlitz" testId="careers-page">
        <PageHero
            eyebrow="Careers"
            title="Earn while you build."
            accent="Then lead."
            intro="Paid internships on real client projects, and a full-time pathway for engineers ready to own delivery. Meritocratic, English-first, and grounded in rural India."
            testId="careers-hero"
        />

        <section className="relative pb-14 lg:pb-20" data-testid="careers-content">
            <div className="max-w-5xl mx-auto px-5 lg:px-10">
                <EnglishGate />

                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold/80 mb-4">Open internships</div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {OPEN_ROLES.map((r, i) => (
                        <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: i * 0.06 }}
                            className="glass rounded-2xl p-6 flex flex-col"
                            data-testid={`role-${r.id}`}
                        >
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="text-ivory text-lg font-display leading-tight">{r.title}</div>
                                <span className="shrink-0 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-gold text-obsidian font-bold">Open</span>
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-gold/80 font-mono mb-3">{r.type}</div>
                            <div className="flex flex-wrap gap-1.5 mb-3">
                                {r.skills.map((s) => (
                                    <span key={s} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-ivory/65">{s}</span>
                                ))}
                            </div>
                            <p className="text-[12px] text-ivory/55 leading-snug flex-1">{r.note}</p>
                            <Link
                                to="/contact"
                                onClick={() => setIntent("web-dev-intern")}
                                className="btn-gold mt-5 !py-2.5 text-sm w-full justify-center"
                                data-testid={`role-cta-${r.id}`}
                            >
                                Apply for internship <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </motion.div>
                    ))}
                </div>

                {/* Associate / Lead pathway */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5 }}
                    className="mt-10 glass rounded-2xl p-7 lg:p-9"
                    data-testid="associate-track"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center">
                            <Compass className="w-5 h-5 text-gold" />
                        </div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-gold/80">Full-time pathway · Lead delivery</div>
                    </div>
                    <h3 className="mt-5 font-display text-3xl text-ivory leading-tight">{ASSOCIATE_TRACK.title}</h3>
                    <p className="mt-3 text-ivory/65 leading-relaxed max-w-2xl">{ASSOCIATE_TRACK.note}</p>

                    <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
                        {ASSOCIATE_TRACK.eligibility.map((e) => (
                            <li key={e} className="flex items-start gap-2 text-sm text-ivory/75">
                                <Check className="w-4 h-4 text-teal mt-0.5 shrink-0" />
                                <span>{e}</span>
                            </li>
                        ))}
                    </ul>

                    <p className="mt-5 text-[12px] text-ivory/45 leading-snug max-w-2xl">
                        Compensation: full salary, equity-eligible at the team level, growth into senior roles as the firm scales with revenue &amp; CSR funding.
                    </p>

                    <Link
                        to="/contact"
                        onClick={() => setIntent("associate-role")}
                        className="btn-ghost mt-6 !py-2.5 !px-5 text-sm"
                        data-testid="associate-cta"
                    >
                        Express interest <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </motion.div>
            </div>
        </section>
    </PageLayout>
);

export default CareersPage;
